// GENERATED FROM mobile_charts@dcbe681 lib/chart-boot-watchdog.js
// sha256=e9d6f0d20fe710460b35ca4f0d6f366b5ecdc8c9ac0ab28ff4da4ce69eea7aa1
// DO NOT EDIT HERE. Edit the mobile_charts original and re-run
// scripts/sync-vendored.sh — the stuck-chart signal and its reason enum are what the shared Loki alert groups by, so both products must classify a failed boot identically.
// Chart boot watchdog — answers "is this user's chart stuck on the loading
// spinner?" without the user having to tell us, and unsticks them when it safely
// can. mobile_charts is the source of truth; flow-web gets a byte-identical
// vendored copy (scripts/sync-vendored.sh).
//
// WHY A WATCHDOG AND NOT JUST BETTER VALIDATION
//
// layout-sanitizer.js removes the shapes that are PROVABLY unrestorable. Two
// failure modes survive it and cannot be validated away: a SHORT anchor list
// (indistinguishable from a legitimately short tool without a per-tool anchor
// count the bundles do not expose), and a drawing-tool chunk that fails to
// download (there the stored data is perfectly valid). Both end the same way —
// the restore throws, nothing catches it, onChartReady never fires.
//
// LOG ON TIME, ACT ON EVIDENCE
//
// A slow cold boot and a hung boot look identical by elapsed time, and reloading
// a merely-slow boot makes it worse. So the triggers are split: the DEADLINE only
// emits telemetry and never changes what the user sees; RECOVERY requires an error
// actually observed inside the restore window. Attribution is by timing, not by
// matching error text, so it survives library upgrades.

// Closed set of causes. Indexed as a Loki label, so every value must stay
// low-cardinality and map to a fix.
export const FAILURE_REASONS = {
    // a drawing crashed the layout restore
    RESTORE_CRASH: 'restore_crash',
    // a lazily-loaded tool chunk never arrived
    CHUNK_LOAD_FAILED: 'chunk_load_failed',
    // the deadline passed with no error observed at all
    UNKNOWN: 'unknown',
};

// Its own reason because the fix is different — retry / cache-bust the asset —
// and because the stored layout is innocent.
const CHUNK_ERROR_PATTERN = /Loading chunk|ChunkLoadError|dynamically imported module|is not loaded|Importing a module script failed/i;

function isChunkError(message) {
    return CHUNK_ERROR_PATTERN.test(String(message || ''));
}

// How long an observed error must go UNRESOLVED before it counts as fatal.
// Boot is the noisiest moment in the app's life: an unrelated rejection from a
// quote fetch or a socket lands inside the restore window routinely (production
// sees ~4.6k window.error + unhandledrejection a day on mobile-charts alone).
// Reloading a chart that was about to be fine costs that user every drawing for
// the session, so the error alone is not enough evidence. Waiting supplies the
// rest: a crashed restore NEVER becomes ready, so this only delays genuinely
// stuck users, while merely-slow charts cancel it themselves via chartReady().
const RECOVERY_GRACE_MS = 5000;

/**
 * @param {object} opts
 * @param {number}   opts.timeoutMs   deadline for onChartReady.
 * @param {function} opts.log         (level, event, attrs) => void
 * @param {function} [opts.recover]   (layoutId, reason) => void. Called AT MOST once,
 *                                    and only when an error was observed inside the
 *                                    restore window. Omit it to run log-only.
 * @param {function} [opts.canRecover] () => boolean, consulted immediately before
 *                                    recovering — for a host that can only safely
 *                                    reload under some conditions.
 * @param {object}   [opts.store]     sessionStorage-like, used to make recovery
 *                                    strictly once per layout per session.
 * @param {number}   [opts.recoveryGraceMs] how long an observed error must go
 *                                    unresolved before recovery runs. Defaults to
 *                                    RECOVERY_GRACE_MS.
 * @param {function} [opts.now]       () => ms, injectable for tests.
 */
export function createChartBootWatchdog(opts) {
    const options = opts || {};
    const timeoutMs = options.timeoutMs || 20000;
    const log = typeof options.log === 'function' ? options.log : function () {};
    const recover = typeof options.recover === 'function' ? options.recover : null;
    const canRecover = typeof options.canRecover === 'function' ? options.canRecover : function () { return true; };
    const store = options.store || null;
    const graceMs = options.recoveryGraceMs === undefined ? RECOVERY_GRACE_MS : options.recoveryGraceMs;
    const now = typeof options.now === 'function' ? options.now : function () { return Date.now(); };

    const startedMs = now();
    let timer = null;
    let graceTimer = null;
    let ready = false;
    let firedDeadline = false;
    let disposed = false;
    // Open from the moment layout content is handed to the library until the chart
    // is ready. An error in here is attributable to the restore; the same error
    // later is somebody else's problem.
    let restoreWindowOpen = false;
    let layoutId = null;
    let observedError = null;      // { message, chunk }
    let recoveryStarted = false;

    function recoveryKey(id) {
        return 'chart.recovered.' + String(id === null || id === undefined ? 'none' : id);
    }

    function alreadyRecovered(id) {
        if (!store) { return false; }
        try { return store.getItem(recoveryKey(id)) === '1'; } catch (_) { return false; }
    }

    function markRecovered(id) {
        if (!store) { return; }
        try { store.setItem(recoveryKey(id), '1'); } catch (_) {}
    }

    function reason() {
        if (observedError) {
            return observedError.chunk ? FAILURE_REASONS.CHUNK_LOAD_FAILED : FAILURE_REASONS.RESTORE_CRASH;
        }
        return FAILURE_REASONS.UNKNOWN;
    }

    function onDeadline() {
        timer = null;
        if (ready || disposed) { return; }
        firedDeadline = true;
        log('error', 'chart.load_failed', {
            reason: reason(),
            layoutId: layoutId === null ? '' : String(layoutId),
            elapsedMs: now() - startedMs,
            // Lets an alert exclude merely-slow boots, and separates
            // "we tried to fix it" from "we just watched".
            recovering: recoveryStarted,
            error: observedError ? String(observedError.message).slice(0, 300) : '',
        });
    }

    // An error inside the restore window is the evidence recovery requires.
    function noteError(message) {
        if (disposed || ready || !restoreWindowOpen) { return; }
        if (!observedError) {
            observedError = { message: message, chunk: isChunkError(message) };
        }
        scheduleRecovery();
    }

    // Arm the grace period once. Re-entrant on purpose: further errors while it is
    // pending change nothing, since the first one already made the case.
    function scheduleRecovery() {
        if (!recover || recoveryStarted || graceTimer !== null) { return; }
        graceTimer = setTimeout(function () {
            graceTimer = null;
            // The chart became ready in the meantime, so the error was survivable
            // and reloading would have destroyed a working session.
            if (ready || disposed) { return; }
            maybeRecover();
        }, graceMs);
    }

    function maybeRecover() {
        if (!recover || recoveryStarted || !observedError) { return; }
        if (alreadyRecovered(layoutId)) {
            // Recovery already ran for this layout in this session and clearly did
            // not help. Do not loop — leave the failure to telemetry.
            log('error', 'chart.load_failed', {
                reason: reason(),
                layoutId: layoutId === null ? '' : String(layoutId),
                elapsedMs: now() - startedMs,
                recovering: false,
                error: 'recovery already attempted',
            });
            return;
        }
        let allowed = false;
        try { allowed = !!canRecover(); } catch (_) { allowed = false; }
        if (!allowed) { return; }
        recoveryStarted = true;
        markRecovered(layoutId);
        try { recover(layoutId, reason()); } catch (_) { /* host problem, not ours */ }
    }

    const errorHandler = function (event) {
        noteError((event && (event.message || (event.error && event.error.message))) || '');
    };
    const rejectionHandler = function (event) {
        const r = event && event.reason;
        noteError((r && (r.message || r)) || '');
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
        window.addEventListener('error', errorHandler);
        window.addEventListener('unhandledrejection', rejectionHandler);
    }
    timer = setTimeout(onDeadline, timeoutMs);

    return {
        /** Layout content has been handed to the library: open the restore window. */
        layoutServed: function (id) {
            if (disposed || ready) { return; }
            layoutId = id;
            restoreWindowOpen = true;
        },

        /**
         * onChartReady fired. Clears the deadline and closes the restore window.
         *
         * Deliberately does NOT emit chart.ready — the host already owns that event,
         * and two emitters would double-count the failure rate's denominator. What
         * only the watchdog knows is chart.recovered_late: ready arrived AFTER the
         * deadline reported a failure, letting an alert subtract slow-but-fine boots.
         */
        chartReady: function () {
            if (disposed || ready) { return; }
            ready = true;
            restoreWindowOpen = false;
            if (timer !== null) { clearTimeout(timer); timer = null; }
            if (graceTimer !== null) { clearTimeout(graceTimer); graceTimer = null; }
            if (firedDeadline) {
                log('warn', 'chart.recovered_late', { readyMs: now() - startedMs });
            }
        },

        /** Test/host hook: report an error without waiting for a window event. */
        noteError: noteError,

        /** Current best-known cause; exported for the host's own logging. */
        reason: reason,

        dispose: function () {
            disposed = true;
            if (timer !== null) { clearTimeout(timer); timer = null; }
            if (graceTimer !== null) { clearTimeout(graceTimer); graceTimer = null; }
            if (typeof window !== 'undefined' && window.removeEventListener) {
                window.removeEventListener('error', errorHandler);
                window.removeEventListener('unhandledrejection', rejectionHandler);
            }
        },
    };
}
